import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Shield, ExternalLink, Loader, Lock } from 'lucide-react';
import { cveAPI } from '../../services/api';
const TechnologyChip = ({ tech, Icon, trialMode = false }) => {
  const [cveData, setCveData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const hideTimeoutRef = useRef(null);

  const technology = tech?.technology || tech?.name || tech;
  const versions = tech?.versions || [];
  const version = versions[0] || null;

  // Check for embedded CVE data from beacon enrichment
  const embeddedCves = tech?.cves || [];

  useEffect(() => {
    // If we already have embedded CVE data from backend enrichment, use it
    if (embeddedCves.length > 0) {
      setCveData({
        has_vulnerabilities: true,
        count: embeddedCves.length,
        vulnerabilities: embeddedCves,
      });
      setLoading(false);
      return;
    }

    // Fallback: live CVE lookup for v1 scans without embedded data
    if (version) {
      checkCVE();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technology, version, embeddedCves.length]);

  const checkCVE = async () => {
    setLoading(true);
    try {
      const response = await cveAPI.check(technology, version);
      setCveData(response.data);
    } catch (error) {
      console.error('Failed to check CVE:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMouseEnter = (e) => {
    if (trialMode) return; // suppress tooltip for trial users
    if (cveData && cveData.has_vulnerabilities) {
      // Clear any pending hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10
      });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    // Add a delay before hiding to allow moving to tooltip
    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 300);
  };

  const handleTooltipMouseEnter = () => {
    // Clear the hide timeout when entering tooltip
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setShowTooltip(true);
  };

  const handleTooltipMouseLeave = () => {
    // Hide immediately when leaving tooltip
    setShowTooltip(false);
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'CRITICAL': 'text-red-500 bg-red-100 border-red-300',
      'HIGH': 'text-orange-500 bg-orange-100 border-orange-300',
      'MEDIUM': 'text-yellow-500 bg-yellow-100 border-yellow-300',
      'LOW': 'text-lime-500 bg-lime-100 border-lime-300',
      'UNKNOWN': 'text-gray-500 bg-gray-100 border-gray-300'
    };
    return colors[severity] || colors['UNKNOWN'];
  };

  const hasVulnerabilities = cveData && cveData.has_vulnerabilities;

  return (
    <>
      <div
        className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full border transition-all hover:scale-105 cursor-pointer relative ${
          hasVulnerabilities && !trialMode
            ? 'bg-red-900/20 border-red-300 text-red-600'
            : hasVulnerabilities && trialMode
            ? 'bg-shinobi-purple/10 border-shinobi-purple/30 text-shinobi-purple'
            : version
            ? 'bg-shinobi-purple/10 border-shinobi-purple/30 text-shinobi-purple'
            : 'bg-slate-100 border-slate-200 text-slate-700'
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Icon className="w-3.5 h-3.5" />
        <span className="font-semibold text-xs">{technology}</span>
        {version && (
          <span className={`text-[10px] px-1 py-0.5 rounded ${
            hasVulnerabilities && !trialMode ? 'bg-red-500/30' : 'bg-shinobi-purple/30'
          }`}>
            v{version}
          </span>
        )}
        {loading && <Loader className="w-3 h-3 animate-spin" />}
        {hasVulnerabilities && !trialMode && <AlertTriangle className="w-3.5 h-3.5" />}
        {hasVulnerabilities && trialMode && <Lock className="w-3 h-3" style={{ color: '#48b94c' }} />}
        {version && !hasVulnerabilities && !loading && <Shield className="w-3.5 h-3.5" />}
      </div>

      {/* Trial upgrade modal */}
      {/* Tooltip */}
      {showTooltip && hasVulnerabilities && !trialMode && (
        <div
          className="fixed z-50 w-96 cyber-card rounded-lg p-4 shadow-2xl border-2 border-red-300"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)'
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h4 className="font-bold text-red-600">
                {cveData.count} Vulnerabilit{cveData.count === 1 ? 'y' : 'ies'} Found
              </h4>
            </div>
            <span className="text-xs text-gray-500">{technology} v{version}</span>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {cveData.vulnerabilities.map((vuln, idx) => (
              <div key={idx} className="bg-slate-50 rounded p-3 border border-slate-200">
                <div className="flex items-start justify-between mb-2">
                  <a
                    href={vuln.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-sm text-shinobi-orange hover:text-shinobi-purple transition-colors flex items-center space-x-1"
                  >
                    <span>{vuln.id}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <div className="flex items-center space-x-2">
                    {vuln.cvss_score && (
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${getSeverityColor(vuln.severity)}`}>
                        {vuln.cvss_score} {vuln.severity}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500 mb-2">{vuln.description}</p>
                {vuln.published && (
                  <p className="text-[10px] text-gray-600">
                    Published: {new Date(vuln.published).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="mt-3 pt-3 border-t border-slate-200">
            <p className="text-xs text-gray-500 text-center">
              Data from National Vulnerability Database (NVD)
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default TechnologyChip;
